// DESIGN-SOURCE: Figma 12 `69:1216` / 29 `247:2762` — the noted mark on a capture row (`70:1342`): an 18x18 `status/success` disc carrying a 10x10 white glyph
/**
 * IconDiscView — a small filled disc with an icon centred in it.
 *
 * It exists because the shape cannot live where it is used. The design draws
 * the capture row's noted mark as a FILLED disc (`radius-full`) rather than a
 * bare glyph, and `rounded-full` written in a feature `*View.tsx` is exactly
 * what `design:budgets` counts as hand-rolled pill styling and refuses to grow.
 * A shared molecule is the sanctioned home for the shape — the ratchet does not
 * scan this directory.
 *
 * A single-use abstraction would normally be the wrong instinct. Here the rule
 * forces it, so the component is deliberately narrow: one tone, one size, no
 * configuration nobody asked for.
 * @module components/molecules/IconDisc/IconDiscView
 */
import React, { memo, type ComponentProps } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";

/** Props for IconDiscView. */
export interface IconDiscViewProps {
  /** The lucide icon to centre in the disc. */
  readonly as: ComponentProps<typeof Icon>["as"];
  /** What the disc means, for assistive technology. */
  readonly accessibilityLabel: string;
  /** Test identity — carried by the disc, never by the Icon (see below). */
  readonly testID?: string;
}

/**
 * Renders an icon inside a filled success disc.
 * @param root0 - Component props
 * @param root0.as - The icon to centre
 * @param root0.accessibilityLabel - What the disc means
 * @param root0.testID - Test identity for the disc
 * @returns The disc element.
 */
const IconDiscView = ({
  as,
  accessibilityLabel,
  testID,
}: IconDiscViewProps) => (
  // The Box carries the test identity, not the Icon: the Icon atom spreads its
  // props over both its wrapper and the SVG it renders, so a testID placed on
  // it resolves to MORE THAN ONE node — RNTL says "found multiple elements" and
  // Playwright's strict mode fails outright.
  <Box
    testID={testID}
    accessibilityLabel={accessibilityLabel}
    className="size-5 shrink-0 items-center justify-center rounded-full bg-status-success"
  >
    <Icon as={as} size="2xs" className="text-content-on-accent" />
  </Box>
);

IconDiscView.displayName = "IconDiscView";

export default memo(IconDiscView);
